import { useCallback, useEffect, useState } from 'react';

/**
 * Check if given ref is overflowing and if it's fully scrolled to left
 * @param handleOverflow - indicator if functionality is active
 * @param ref - scroll reference
 * @param overflowOffset - offset to prevent overflowing where not needed
 */
export const useOverflowControl = (handleOverflow, ref, overflowOffset = 15) => {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [fullyScrolledLeft, setFullyScrolledLeft] = useState(false);
  const [startedScrolling, setStartedScrolling] = useState(false);
  const checkOverflow = useCallback(() => setIsOverflowing((ref.current?.scrollWidth ?? 0) - overflowOffset > (ref.current?.clientWidth ?? 0)), [setIsOverflowing, ref, overflowOffset]);
  const checkScrollPosition = useCallback(() => {
    setFullyScrolledLeft((ref.current?.scrollWidth ?? 0) <= (ref.current?.clientWidth ?? 0) + (ref.current?.scrollLeft ?? 0));
    setStartedScrolling((ref.current?.scrollLeft ?? 0) > 0);
  }, [setFullyScrolledLeft, setStartedScrolling, ref]);
  useEffect(() => {
    if (!ref.current || !handleOverflow) {
      return;
    }
    checkOverflow();
    const wrapper = ref.current;

    // call checkOverflow after resize (lazy with a small delay to prevent multiple checks)
    let overflowCheckTimeout;
    let observer;
    const lazyOverflowCheck = () => {
      window.clearTimeout(overflowCheckTimeout);
      overflowCheckTimeout = window.setTimeout(checkOverflow, 200);
    };

    // Use resize observer if supported, to detect div resize instead of window resize
    if (ResizeObserver) {
      observer = new ResizeObserver(lazyOverflowCheck);
      observer.observe(wrapper);
    } else {
      window.addEventListener('resize', lazyOverflowCheck);
    }
    lazyOverflowCheck();

    // call checkScrollPosition after scrolling (similar to checkOverflow)
    let scrollCheckTimeout;
    const lazyScrollCheck = () => {
      window.clearTimeout(scrollCheckTimeout);
      scrollCheckTimeout = window.setTimeout(checkScrollPosition, 50);
    };
    wrapper.addEventListener('scroll', lazyScrollCheck);
    lazyScrollCheck();
    return () => {
      window.clearTimeout(overflowCheckTimeout);
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener('resize', lazyOverflowCheck);
      }
      wrapper.removeEventListener('scroll', lazyScrollCheck);
    };
  }, [ref, handleOverflow, checkOverflow, checkScrollPosition]);
  return [isOverflowing, fullyScrolledLeft, startedScrolling];
};